import React, { useRef, useState } from 'react';
import { X, Download, Copy, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const formatINR = (value) =>
  `₹${(parseFloat(value) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const QuotePreview = ({ member, claim, pricing, onClose }) => {
  const quoteRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const scopeItems = (pricing.complexityLines || []).map((item) =>
    item.value === 1 ? item.name : `${item.name}: ${item.value}`
  );

  const captureCanvas = async () => {
    if (!quoteRef.current) return null;
    return html2canvas(quoteRef.current, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true
    });
  };

  const handleCopyImage = async () => {
    setBusy(true);
    try {
      const canvas = await captureCanvas();
      if (!canvas) throw new Error('Unable to capture quote');
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Unable to create image');

      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        toast.success('Quote image copied. Paste it in WhatsApp.');
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${member.name || 'quote'}-quote.png`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Clipboard unsupported. Quote image downloaded instead.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to copy quote image');
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    setBusy(true);
    try {
      const canvas = await captureCanvas();
      if (!canvas) throw new Error('Unable to capture quote');
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 16;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 8;

      pdf.addImage(imgData, 'PNG', 8, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 16;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 8;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 8, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - 16;
      }

      pdf.save(`${member.name || 'customer'}-pf-quote.pdf`);
      toast.success('Quote PDF downloaded');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Customer Quote</h3>
            <p className="text-xs text-slate-500">Copy as image for WhatsApp or download PDF</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
          <div
            ref={quoteRef}
            className="bg-white rounded-xl border border-slate-200 p-6 text-slate-900 shadow-sm"
            style={{ fontFamily: 'Segoe UI, Arial, sans-serif' }}
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-primary-600 font-semibold">Paytiemp</div>
                <h2 className="text-2xl font-bold mt-1">PF Service Proposal</h2>
                <p className="text-sm text-slate-500 mt-1">Professional claim assistance quotation</p>
              </div>
              <div className="text-right text-sm">
                <div className="font-medium">{new Date().toLocaleDateString('en-IN')}</div>
                <div className="text-slate-500">Quote ref: {claim?.id || member?.id || 'NEW'}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs">Member</div>
                <div className="font-semibold">{member?.name || '-'}</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs">UAN / PF Account</div>
                <div className="font-semibold">{member?.uan || '-'}</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs">Estimated PF Balance</div>
                <div className="font-semibold">
                  {formatINR(pricing.pfBalance || 0)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="text-slate-500 text-xs">Estimated Timeline</div>
                <div className="font-semibold">{pricing.timelineLabel}</div>
              </div>
            </div>

            <div className="mt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700 mb-2">Scope of Service</h4>
              {scopeItems.length === 0 ? (
                <p className="text-sm text-slate-500">No activities selected.</p>
              ) : (
                <ul className="space-y-1.5">
                  {scopeItems.map((item) => (
                    <li key={item} className="text-sm flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary-600 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-5 rounded-xl bg-primary-50 border border-primary-100 p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-slate-500 text-xs">Final Agreed Amount</div>
                  <div className="text-xl font-bold text-primary-700">{formatINR(pricing.finalAgreedFee || 0)}</div>
                </div>
                <div>
                  <div className="text-slate-500 text-xs">Initial Advance (case-specific)</div>
                  <div className="text-xl font-bold text-slate-900">{formatINR(pricing.initialAdvance || 0)}</div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700 mb-2">Terms & Conditions</h4>
              <ol className="space-y-1.5 list-decimal list-inside">
                {(pricing.quoteTerms || []).map((term) => (
                  <li key={term} className="text-sm text-slate-700 leading-relaxed">{term}</li>
                ))}
              </ol>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500 flex justify-between">
              <span>Prepared for {member?.name || 'Customer'}</span>
              <span>Paytiemp PF Claims Desk</span>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-white">
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
          <button type="button" disabled={busy} onClick={handleCopyImage} className="btn-secondary inline-flex items-center">
            <Copy className="w-4 h-4 mr-2" />
            Copy Image
          </button>
          <button type="button" disabled={busy} onClick={handleDownloadPdf} className="btn-primary inline-flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </button>
          <div className="w-full text-xs text-slate-500 flex items-center gap-1 justify-end mt-1">
            <ImageIcon className="w-3.5 h-3.5" />
            Tip: Copy Image, then paste directly into WhatsApp.
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotePreview;
